import React from "react";
import { gql } from "@apollo/client";
import { Field } from "react-final-form";
import { useQuery } from "@apollo/client";
import isEqual from "lodash.isequal";
import { Link } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";

import {
  Alert,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  IconButton,
  Paper,
} from "@mui/material";
import { Pencil as EditIcon, Paw as SolidouIcon } from "mdi-material-ui";

import Layout from "components/common/Layout";
import SearchInput from "components/common/SearchInput";

function LegacyMouthpieceDetails({ mouthpiece }) {
  const params = [
    mouthpiece.params.design,
    mouthpiece.params.shank && `Tenon : ${mouthpiece.params.shank}`,
    mouthpiece.params.volume && `Volume: ${mouthpiece.params.volume}`,
  ]
    .filter((v) => !!v)
    .join(" - ");

  return <>{params}</>;
}

const QUERY = gql`
  query (
    $first: Int
    $after: String
    $search: String
    $instrument: String
    $instrumentType: String
  ) {
    mouthpieces(
      first: $first
      after: $after
      search: $search
      instrument: $instrument
      instrumentType: $instrumentType
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }

      edges {
        cursor
        node {
          type
          name

          legacyMouthpiece {
            id
            params {
              design
              shank
              volume
            }
          }

          saxophoneMouthpiece {
            id
          }

          clarinetMouthpiece {
            id
          }
        }
      }
    }
  }
`;

export default function MouthpieceSearch({ instrument, instrumentType }) {
  const [search, setSearch] = React.useState("");

  const { data, loading, fetchMore } = useQuery(QUERY, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    variables: {
      first: 20,
      search,
      instrument,
      instrumentType,
    },
  });

  return (
    <Field
      name="search.selectedMouthpiece"
      component="input"
      type="hidden"
      validate={(value) => {
        if (!value) return true;
      }}
    >
      {({ input, meta }) => (
        <Box
          sx={{
            flexGrow: 1,
            m: (t) => t.spacing(-2, -3),
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Paper square sx={{ p: (t) => t.spacing(2, 3), flexShrink: 0 }}>
            <SearchInput onChange={setSearch} />
          </Paper>

          <List
            disablePadding
            id="MouthpieceSearch-list"
            sx={{ flexGrow: 1, overflow: "auto" }}
          >
            <InfiniteScroll
              scrollableTarget="MouthpieceSearch-list"
              dataLength={data?.mouthpieces.edges.length ?? 0}
              next={() =>
                fetchMore({
                  variables: { after: data?.mouthpieces.pageInfo.endCursor },
                })
              }
              loader={<Layout.Loading />}
              hasMore={data?.mouthpieces.pageInfo.hasNextPage ?? loading}
            >
              {data?.mouthpieces.edges.map(({ cursor, node: mouthpiece }) => (
                <ListItem
                  key={cursor}
                  dense
                  button
                  onClick={() => input.onChange(mouthpiece)}
                >
                  <Checkbox
                    checked={isEqual(input.value, mouthpiece)}
                    tabIndex={-1}
                    disableRipple
                  />

                  <ListItemText
                    primary={mouthpiece.name}
                    secondary={
                      mouthpiece.legacyMouthpiece && (
                        <LegacyMouthpieceDetails
                          mouthpiece={mouthpiece.legacyMouthpiece}
                        />
                      )
                    }
                  />

                  {mouthpiece.type === "legacyMouthpiece" ? (
                    <ListItemSecondaryAction>
                      <IconButton
                        component={Link}
                        to={`/mouthpieces/legacy/${mouthpiece.legacyMouthpiece.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="large"
                      >
                        <EditIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  ) : (
                    <ListItemSecondaryAction>
                      <IconButton disabled size="large">
                        <SolidouIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
            </InfiniteScroll>
          </List>

          {meta.touched && meta.error && (
            <Alert severity="error">Choisissez un bec</Alert>
          )}
        </Box>
      )}
    </Field>
  );
}

export function isAvailable() {
  return true;
}

export const tabName = "search";

export function TabLabel() {
  return <span>Recherche</span>;
}

export async function handleSubmit(values, client) {
  const mouthpiece = values?.search?.selectedMouthpiece;

  return {
    type: mouthpiece.type,
    legacyMouthpiece: mouthpiece.legacyMouthpiece?.id,
    saxophoneMouthpiece: mouthpiece.saxophoneMouthpiece?.id,
    clarinetMouthpiece: mouthpiece.clarinetMouthpiece?.id,
  };
}
